import { createSlice } from '@reduxjs/toolkit'
import {
  createSystemAdminUsers,
  editSystemAdminUser,
  filterSystemAdminUsers,
  retrieveSystemAdminUserByID,
  retrieveSystemAdminUsers,
  retrieveSystemAdminUsersCompanies,
  systemAdminUsersChangeStatus,
  systemAdminUsersResetAlertHandler,
  updateSelectedSystemAdminUserStore,
  updateTenantID,
} from './SystemAdminUsersThunk'
import { objNotEmpty, updateStateFromQueries } from '../../../../utils/helpers'

const defaultPageSize = 9
const defaultActiveSummary = 'ALL'
const initialState = {
  loading: false,
  pageNumber: 0,
  pageSize: defaultPageSize,
  finalPage: false,
  summaries: {},
  activeSummary: defaultActiveSummary,
  sortBy: [],
  filterList: [],
  activeFilters: [],
  activeSort: {},
  activeSearch: '',
  data: [],
  companiesValue: [],
  updatedUser: {},
  tenant_id: null,
  alert: {},
}

const SystemAdminUsersSlice = createSlice({
  name: 'systemAdminUsers',
  initialState,

  extraReducers: (builder) => {
    builder.addCase(systemAdminUsersResetAlertHandler.fulfilled, (state) => {
      state.alert = {}
    })
    builder.addCase(retrieveSystemAdminUsers.pending, (state, { meta: { arg: { queries } } }) => {
      state.loading = true

      state.pageNumber = queries?.pageNumber ? queries?.pageNumber : 0
      if (!queries?.pageNumber) {
        state.data = []
      }
    })
    builder.addCase(retrieveSystemAdminUsers.fulfilled, (state, { payload }) => {
      const { response, queries } = payload
      state.loading = false
      state.data = queries?.pageNumber === 0 ? response.users : [...state.data, ...response.users]
      state.summaries = response.metadata
      state.finalPage = response.users.length < state.pageSize
      state.sortBy = response.sort
      state.activeSort =
        (objNotEmpty(state.activeSort) && state.pageNumber >= 1)
          ? state.activeSort : response?.sort?.[0]
      state.loading = false
      state.activeSearch = queries?.activeSearch ?? state.activeSearch
      state.filterList = normalizeSystemAdminFacets(response.facets)
      state.activeFilters = queries?.activeFilters ?? state.activeFilters
      updateStateFromQueries(state, queries)
    })
    builder.addCase(retrieveSystemAdminUsers.rejected, (state, { payload }) => {
      state.loading = false
      state.alert = payload
    })
    builder.addCase(filterSystemAdminUsers.pending, (state) => {
      state.loading = true
      state.pageNumber = 0
      state.data = []
    })
    builder.addCase(filterSystemAdminUsers.fulfilled, (state, { payload }) => {
      const { response, queries } = payload
      state.loading = false
      state.data = response.users
      state.summaries = response.metadata
      state.finalPage = response.users.length < state.pageSize
      state.pageNumber = 0
      state.sortBy = response.sort
      state.activeSort = state.activeSort || response.sort[0]

      state.filterList = normalizeSystemAdminFacets(response.facets)
      state.activeFilters = queries?.activeFilters || state.activeFilters
    })
    builder.addCase(filterSystemAdminUsers.rejected, (state, { payload }) => {
      state.loading = false
      state.alert = payload
    })
    builder.addCase(updateTenantID.pending, (state) => {
      state.loading = false
    })
    builder.addCase(updateTenantID.rejected, (state, { payload }) => {
      state.loading = false
      state.alert = payload
    })
    builder.addCase(updateTenantID.fulfilled, (state, { payload }) => {
      state.loading = false
      state.tenant_id = payload
    })
    builder.addCase(systemAdminUsersChangeStatus.pending, (state) => {
      state.loading = true
      state.finalPage = true
    })
    builder.addCase(systemAdminUsersChangeStatus.fulfilled, (state) => {
      state.pageNumber = 0
      state.loading = false
    })
    builder.addCase(systemAdminUsersChangeStatus.rejected, (state, { payload }) => {
      state.loading = false
      state.alert = payload
    })
    builder.addCase(createSystemAdminUsers.pending, (state) => {
      state.loading = true
      state.alert = { }
    })
    builder.addCase(createSystemAdminUsers.rejected, (state, { payload }) => {
      state.loading = false
      state.alert = payload
    })
    builder.addCase(createSystemAdminUsers.fulfilled, (state, { payload }) => {
      state.loading = false
      state.updatedUser = {}
      state.alert = payload
    })
    builder.addCase(editSystemAdminUser.pending, (state) => {
      state.loading = true
      state.alert = { }
    })
    builder.addCase(editSystemAdminUser.rejected, (state, { payload }) => {
      state.loading = false
      state.alert = payload
    })
    builder.addCase(editSystemAdminUser.fulfilled, (state, { payload }) => {
      state.loading = false
      state.updatedUser = {}
      state.alert = payload
    })
    builder.addCase(retrieveSystemAdminUsersCompanies.pending, (state) => {
      state.loading = true
    })
    builder.addCase(retrieveSystemAdminUsersCompanies.rejected, (state, { payload }) => {
      state.loading = false
      state.alert = payload
    })
    builder.addCase(retrieveSystemAdminUsersCompanies.fulfilled, (state, { payload }) => {
      state.loading = false
      state.companiesValue = payload
    })
    builder.addCase(retrieveSystemAdminUserByID.pending, (state) => {
      state.loading = true
    })
    builder.addCase(retrieveSystemAdminUserByID.rejected, (state, { payload }) => {
      state.loading = false
      state.alert = payload
    })
    builder.addCase(retrieveSystemAdminUserByID.fulfilled, (state, { payload }) => {
      state.loading = false
      state.updatedUser = payload
      state.updatedUser.departments = payload.employee?.departments
    })
    builder.addCase(updateSelectedSystemAdminUserStore.pending, (state) => {
      state.loading = true
    })
    builder.addCase(updateSelectedSystemAdminUserStore.rejected, (state, { payload }) => {
      state.loading = false
      state.alert = payload
    })
    builder.addCase(updateSelectedSystemAdminUserStore.fulfilled, (state, { payload }) => {
      state.loading = false
      state.updatedUser = payload
    })
  },
})
export const selectSystemAdminUsers = ({ systemAdminUsers }) => systemAdminUsers

export default SystemAdminUsersSlice.reducer

function normalizeSystemAdminFacets(facets = {}) {
  return {
    userTypes: normalizeFacetItems(facets?.user_types || []),
    companies: normalizeFacetItems(facets?.companies || []),
    subscriptionReminder: normalizeFacetItems(facets?.subscription_reminder_statuses || []),
  }
}

function normalizeFacetItems(items = []) {
  return (items || []).map((item) => ({
    id: item?.key ?? item?.id ?? '',
    name: item?.name ?? item?.value ?? '',
    key: item?.key ?? item?.id,
  }))
}
